import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { MagnifyingGlass, Plus, WhatsappLogo, Phone, Sparkle, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUSES = ["New", "Contacted", "Interested", "Site Visit", "Negotiation", "Token", "Registry", "Closed", "Lost"];
const STATUS_STYLES = {
  New: "text-sky-300 bg-sky-400/10 border-sky-400/20",
  Contacted: "text-indigo-300 bg-indigo-400/10 border-indigo-400/20",
  Interested: "text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/20",
  "Site Visit": "text-cyan-300 bg-cyan-400/10 border-cyan-400/20",
  Negotiation: "text-amber-300 bg-amber-400/10 border-amber-400/20",
  Token: "text-purple-300 bg-purple-400/10 border-purple-400/20",
  Registry: "text-fuchsia-300 bg-fuchsia-400/10 border-fuchsia-400/20",
  Closed: "text-emerald-300 bg-emerald-400/10 border-emerald-400/20",
  Lost: "text-rose-300 bg-rose-400/10 border-rose-400/20",
};

const PRIORITY_STYLES = {
  High: "text-rose-300 bg-rose-400/10 border-rose-400/20",
  Medium: "text-amber-300 bg-amber-400/10 border-amber-400/20",
  Low: "text-slate-300 bg-slate-400/10 border-slate-400/20",
};

function LeadForm({ initial, users, onSave, onClose }) {
  const [data, setData] = useState(initial || { name: "", mobile: "", whatsapp: "", email: "", budget: "", property_type: "Villa", location: "", project: "", source: "Website", assigned_to: "", status: "New", priority: "Medium", notes: "" });
  const set = (k, v) => setData(d => ({ ...d, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      const amt = data.sale_amount;
      await onSave({ ...data, sale_amount: amt === "" || amt == null ? null : Number(amt), sale_date: data.sale_date || null });
      toast.success("Lead saved");
      onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-[#0A1120] border border-[#1E293B] rounded-xl w-full max-w-2xl max-h-[90vh] overflow-auto" data-testid="lead-form-modal">
        <div className="p-5 border-b border-[#1E293B] flex items-center justify-between">
          <h3 className="font-serif text-xl text-white">{initial ? "Edit Lead" : "New Lead"}</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            ["Name*", "name", "text", true],
            ["Mobile*", "mobile", "text", true],
            ["WhatsApp", "whatsapp", "text"],
            ["Email", "email", "email"],
            ["Budget", "budget", "text"],
            ["Location", "location", "text"],
            ["Project", "project", "text"],
            ["Sale Amount (₹, when Closed)", "sale_amount", "number"],
            ["Sale Date", "sale_date", "date"],
          ].map(([label, key, type, req]) => (
            <div key={key}>
              <label className="text-xs uppercase tracking-widest text-slate-400">{label}</label>
              <input required={req} type={type} value={data[key] || ""} onChange={e => set(key, e.target.value)}
                data-testid={`lead-field-${key}`}
                className="mt-1 w-full bg-[#060B14] border border-[#1E293B] focus:border-[#D4AF37] outline-none rounded-md px-3 py-2 text-white text-sm" />
            </div>
          ))}
          {[
            ["Property Type", "property_type", ["Villa", "Apartment", "Plot", "Penthouse", "Commercial"]],
            ["Source", "source", ["Website", "Facebook", "Instagram", "Google Ads", "Referral", "Walk-in", "Direct"]],
            ["Status", "status", STATUSES],
            ["Priority", "priority", ["High", "Medium", "Low"]],
          ].map(([label, key, opts]) => (
            <div key={key}>
              <label className="text-xs uppercase tracking-widest text-slate-400">{label}</label>
              <select value={data[key] || ""} onChange={e => set(key, e.target.value)} data-testid={`lead-select-${key}`}
                className="mt-1 w-full bg-[#060B14] border border-[#1E293B] focus:border-[#D4AF37] outline-none rounded-md px-3 py-2 text-white text-sm">
                {opts.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          ))}
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-400">Assigned Executive</label>
            <select value={data.assigned_to || ""} onChange={e => set("assigned_to", e.target.value)} data-testid="lead-select-assigned"
              className="mt-1 w-full bg-[#060B14] border border-[#1E293B] focus:border-[#D4AF37] outline-none rounded-md px-3 py-2 text-white text-sm">
              <option value="">— Unassigned —</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-widest text-slate-400">Notes</label>
            <textarea rows={3} value={data.notes || ""} onChange={e => set("notes", e.target.value)}
              data-testid="lead-field-notes"
              className="mt-1 w-full bg-[#060B14] border border-[#1E293B] focus:border-[#D4AF37] outline-none rounded-md px-3 py-2 text-white text-sm" />
          </div>
        </div>
        <div className="p-5 border-t border-[#1E293B] flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-300 hover:text-white">Cancel</button>
          <button type="submit" data-testid="lead-save-btn" className="px-4 py-2 rounded-md bg-[#D4AF37] text-[#060B14] font-semibold text-sm hover:bg-[#F2CE6E]">Save Lead</button>
        </div>
      </form>
    </div>
  );
}

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [scoringId, setScoringId] = useState(null);

  const load = async () => {
    const params = {};
    if (search) params.search = search;
    if (statusFilter) params.status = statusFilter;
    const r = await api.get("/leads", { params });
    setLeads(r.data);
  };

  useEffect(() => { load(); }, [search, statusFilter]);
  useEffect(() => { api.get("/users").then(r => setUsers(r.data)).catch(() => {}); }, []);

  const save = async (data) => {
    if (editing) await api.patch(`/leads/${editing.id}`, data);
    else await api.post("/leads", data);
    await load();
  };

  const scoreAI = async (id) => {
    setScoringId(id);
    try {
      const r = await api.post(`/ai/lead-score/${id}`);
      toast.success(`Score: ${r.data.score} • ${r.data.tier}`, { description: r.data.next_action });
      await load();
    } catch { toast.error("AI scoring failed"); }
    finally { setScoringId(null); }
  };

  const userMap = Object.fromEntries(users.map(u => [u.id, u.name]));

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Pipeline</div>
          <h1 className="font-serif text-3xl md:text-4xl text-white mt-1">Lead Management</h1>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="new-lead-btn"
          className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#D4AF37] text-[#060B14] font-semibold text-sm hover:bg-[#F2CE6E] transition-colors">
          <Plus weight="bold" /> New Lead
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input placeholder="Search name, mobile, email, project…" value={search} onChange={e => setSearch(e.target.value)}
            data-testid="lead-search-input"
            className="w-full bg-[#0A1120] border border-[#1E293B] focus:border-[#D4AF37] outline-none rounded-md pl-10 pr-4 py-2.5 text-sm text-white" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="lead-filter-status"
          className="bg-[#0A1120] border border-[#1E293B] focus:border-[#D4AF37] outline-none rounded-md px-4 py-2.5 text-sm text-white">
          <option value="">All Status</option>
          {STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
      </div>

      <div className="card-luxury overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#060B14] border-b border-[#1E293B]">
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-400">
                <th className="px-4 py-3">Lead</th>
                <th className="px-4 py-3">Project / Budget</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Priority</th>
                <th className="px-4 py-3">Assigned</th>
                <th className="px-4 py-3">AI Score</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 && (
                <tr><td colSpan={7} className="text-center py-10 text-slate-500">No leads yet. Create your first lead.</td></tr>
              )}
              {leads.map(l => (
                <tr key={l.id} data-testid="lead-row" className="border-b border-[#1E293B] hover:bg-white/[0.02]">
                  <td className="px-4 py-3">
                    <Link to={`/leads/${l.id}`} className="text-white hover:text-[#D4AF37] font-medium">{l.name}</Link>
                    <div className="text-xs text-slate-400">{l.mobile}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-white">{l.project || "—"}</div>
                    <div className="text-xs text-[#D4AF37]">{l.budget}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${STATUS_STYLES[l.status] || ""}`}>{l.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${PRIORITY_STYLES[l.priority] || ""}`}>{l.priority}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-300">{userMap[l.assigned_to] || "—"}</td>
                  <td className="px-4 py-3">
                    {l.ai_score ? (
                      <span className="text-[#D4AF37] font-bold">{l.ai_score.score} <span className="text-xs opacity-70">{l.ai_score.tier}</span></span>
                    ) : (
                      <button onClick={() => scoreAI(l.id)} disabled={scoringId === l.id} data-testid={`ai-score-btn-${l.id}`}
                        className="text-xs flex items-center gap-1 text-[#D4AF37] border border-[#D4AF37]/30 rounded-full px-3 py-1 hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-50">
                        <Sparkle size={12} weight="duotone" /> {scoringId === l.id ? "…" : "Score"}
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      {l.whatsapp && (
                        <a href={`https://wa.me/${l.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" title="WhatsApp" data-testid={`whatsapp-${l.id}`}
                           className="text-emerald-400 hover:text-emerald-300"><WhatsappLogo size={18} weight="duotone" /></a>
                      )}
                      <a href={`tel:${l.mobile}`} title="Call" className="text-sky-400 hover:text-sky-300"><Phone size={18} weight="duotone" /></a>
                      <button onClick={() => { setEditing(l); setShowForm(true); }} data-testid={`edit-lead-${l.id}`}
                        className="text-xs text-slate-300 hover:text-[#D4AF37]">Edit</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && <LeadForm initial={editing} users={users} onSave={save} onClose={() => setShowForm(false)} />}
    </div>
  );
}
